
import { forkJoin as observableForkJoin, Observable } from 'rxjs';
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { ENVIRONMENT } from '../../environments/environment';
import CONFIGURATION from '../../configurations/configuration';

import { AuthService } from './auth.service';
import { BaseService } from './base.service';

@Injectable()
export class LoanService {
  ekycEsignServiceUrl: string = ENVIRONMENT.service.ees;
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private baseService: BaseService,
  ) {
  }

  // Loan
  acceptLoanOffer(loanId: number, status: string): Observable<any> {
    const url = ENVIRONMENT.service.loan + '/loan/accept/me/' + loanId;
    return this.baseService.put(url, { loan_status: status });
  }

  getLoans(): Observable<any> {
    const url = ENVIRONMENT.service.loan + '/loan/user/me';
    return this.baseService.get(url);
  }

  getLoanTypes(): Observable<any> {
    const url = ENVIRONMENT.service.loan + '/type?showed=1&country_id=' + CONFIGURATION.country_code;
    return this.baseService.get(url);
  }

  getLoanTypeDetail(loanTypeId: number): Observable<any> {
    const url = ENVIRONMENT.service.loan + '/type/' + loanTypeId + '?country_id=' + CONFIGURATION.country_code;
    return this.baseService.get(url);
  }

  submitLoan(body: any): Observable<any> {
    const url = ENVIRONMENT.service.loan + '/loan/simple_loan';
    return this.baseService.post(url, body);
  }

  submitLoanRecourses(data: Array<any>, loanId: number): Observable<any> {
    const url = ENVIRONMENT.service.loan + '/loan/loan_recourse/' + loanId;
    const observableBatch = new Array<any>();
    data.forEach(body => {
      observableBatch.push(
        this.baseService.post(url, body)
      );
    });
    return observableForkJoin(observableBatch);
  }

  getBorrowerLoans(): Observable<any> {
    const url = `${ENVIRONMENT.service.loanV2}/borrower/me/loans`;
    return this.baseService.get(url);
  }

  getLoanAmountRange(countryId: string, group: string = ''): Observable<any> {
    const url = ENVIRONMENT.service.loanV2 + '/master/enums/loan_amount_range?country_code=' + countryId + '&group=' + group;
    return this.baseService.get(url);
  }

  getLoanCbsRating(): Observable<any> {
    const url = ENVIRONMENT.service.loanV2 + '/master/enums/cbs_rating';
    return this.baseService.get(url);
  }

  getDocumentToSign(loanId: string, productId: string = '', offeringType: string = 'LOAN'): Observable<any> {
    let params = new HttpParams();
    params = params.append('offering_id', loanId);
    params = params.append('offering_type', offeringType);
    if (productId) {
      params = params.append('offering_product_id', productId);
    }
    const url = `${ENVIRONMENT.service.v2}/me/kyc/documents_to_sign?${params.toString()}`;
    return this.baseService.get(url);
  }

  getDocumentStatus(documentId: string) {
    const body = {};
    const url = `${ENVIRONMENT.service.v2}/me/kyc/documents_to_sign/${documentId}`;
    return this.baseService.put(url, body);
  }

  getPowerOfAttorneyDocument(loanId: string) {
    const token = this.authService.getBearerToken();
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      }),
      observe: 'response' as 'body',
      responseType: 'response' as 'text'
    };
    const url = `${ENVIRONMENT.service.v2}/me/power_of_attorneys/${loanId}`;
    return this.http.get(url, httpOptions);
  }

  getFacilityList(count: boolean = false, showRemaining: boolean = false, page: number = 1, recordsPerPage: number = 10): Observable<any> {
    const url = `${ENVIRONMENT.service.facility}/digisign?count=${count}&page=${page}&size=${recordsPerPage}&show_remaining=${showRemaining}`;
    return this.baseService.get(url);
  }

  getLiveFacilityList(showRemaining: boolean = false, page: number = 1, recordsPerPage: number = 10): Observable<any>{
    const url = `${ENVIRONMENT.service.bbs}/borrower-dashboard/live-facilities?page=${page}&showRemaining=${showRemaining}&recordsPerPage=${recordsPerPage}`
    return this.baseService.get(url)
  }

  getVidaEsignDetails(referenceType: 'LOAN' | 'FACILITY', reference: string = undefined, offerStatus: string = undefined, previewDocument: boolean = true): Observable<any> {
    const params = new URLSearchParams();
    params.set('referenceType', referenceType);
    if (reference) {
      params.set('reference', reference);
    }
    if (offerStatus) {
      params.set('offerStatus', offerStatus)
    }
    if (previewDocument) {
      params.set('previewDocument', String(previewDocument))
    }
    const url = `${this.ekycEsignServiceUrl}/digital-signature-session/offer?${params.toString()}`;
    return this.baseService.get(url);
  }

  startDigitalSignatureSession(reference: string, referenceType: 'LOAN' | 'FACILITY'): Observable<any> {
    const url = `${this.ekycEsignServiceUrl}/digital-signature-session/start/id`;
    return this.baseService.post(url, {
      reference,
      referenceType
    })
  }

  submitDigitalSignatureSession(reference: string, referenceType: 'LOAN' | 'FACILITY'): Observable<any> {
    const url = `${this.ekycEsignServiceUrl}/digital-signature-session/submit/id`;
    return this.baseService.post(url, {
      reference,
      referenceType
    })
  }

  rejectDigitalSignatureSession(reference: string, referenceType: 'LOAN' | 'FACILITY'): Observable<any> {
    const url = `${this.ekycEsignServiceUrl}/digital-signature-session/reject/id`;
    return this.baseService.post(url, {
      reference,
      referenceType
    })
  }
}
